/**
 * Global merchant state model
 * Manages the currently selected merchant (partner) for multi-merchant users
 *
 * localStorage keys are user-specific to avoid cross-user data leakage:
 * - aimerpay_merchant_{userId} - selected merchant ID for user
 */

import { history } from '@umijs/max';
import { useCallback, useRef, useState } from 'react';
import { getPartners } from '@/services/business/partner';
import { getEnteredMerchantId } from './merchantWorkspace';

// Helper to get user-specific storage key
const getStorageKey = (userId: string | undefined, suffix: string) => {
  return userId ? `aimerpay_${suffix}_${userId}` : `aimerpay_${suffix}`;
};

/** Shape persisted in `aimerpay_merchants_{userId}` (see getCurrentMerchantRole) */
type StoredMerchant = { id: number; myRole?: string };

/**
 * Current merchant's per-partner role, read straight from localStorage.
 *
 * access.ts is a plain function of initialState and can't reach this model, so
 * it reads the same storage the model writes — same trick as checkHasMerchants.
 * Returns undefined when unknown (legacy number[] payload, or SuperAdmin/Admin
 * acting-as, whose merchants carry no myRole); callers must treat undefined as
 * "not restricted".
 */
export function getCurrentMerchantRole(userId?: string): string | undefined {
  if (!userId) return undefined;
  try {
    const currentId = Number.parseInt(
      localStorage.getItem(getStorageKey(userId, 'merchant')) || '',
      10,
    );
    if (Number.isNaN(currentId)) return undefined;

    const raw = localStorage.getItem(getStorageKey(userId, 'merchants'));
    if (!raw) return undefined;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return undefined;

    // Legacy payload was number[] — no role information available.
    const entry = list.find(
      (m): m is StoredMerchant =>
        typeof m === 'object' && m !== null && m.id === currentId,
    );
    return entry?.myRole;
  } catch {
    return undefined;
  }
}

/**
 * Merchant info for UI display
 * Derived from BusinessAPI.Partner
 */
export interface MerchantInfo {
  id: number;
  partnerName: string;
  legalName?: string;
  type: 'organization' | 'individual';
  status: BusinessAPI.PartnerStatus;
  email: string;
  phone?: string;
  hasAdyenAccountHolder: boolean;
  balanceAccountCount: number;
  /**
   * Current user's role on this partner (from my-partners).
   * 'MerchantOwner' = own; 'PartnerOwner'/'PartnerViewer' = granted by another owner.
   */
  myRole?: string;
}

/**
 * Which merchant a freshly-loaded list should start on, after a page reload.
 *
 * The entered workspace wins over the last-selected id: `changeMerchant` also
 * fires from the partner detail page (which is *not* entering a workspace), so
 * `aimerpay_merchant_{userId}` can point somewhere the user never entered.
 * Every candidate is validated against the list before it is accepted, so a
 * stale id degrades to the next source instead of leaving nothing selected.
 *
 * Must be called *before* loadMerchants — that writes `aimerpay_merchant_*`.
 */
export function resolveInitialMerchantId(
  merchantList: MerchantInfo[],
  userId?: string,
): number | undefined {
  const entered = getEnteredMerchantId(userId);
  if (entered && merchantList.some((m) => m.id === entered)) return entered;

  const raw = localStorage.getItem(getStorageKey(userId, 'merchant'));
  const saved = raw ? Number.parseInt(raw, 10) : Number.NaN;
  if (!Number.isNaN(saved) && merchantList.some((m) => m.id === saved))
    return saved;

  return merchantList[0]?.id;
}

/**
 * Convert BusinessAPI.Partner to MerchantInfo for UI
 */
function partnerToMerchantInfo(partner: BusinessAPI.Partner): MerchantInfo {
  return {
    id: partner.id,
    partnerName: partner.partnerName,
    legalName: partner.legalName,
    type: partner.type,
    status: partner.status,
    email: partner.email,
    phone: partner.phone,
    hasAdyenAccountHolder: partner.hasAdyenAccountHolder,
    balanceAccountCount: partner.balanceAccountCount,
    myRole: partner.myRole,
  };
}

export default function useMerchantModel() {
  const [currentMerchant, setCurrentMerchant] = useState<MerchantInfo | null>(
    null,
  );
  const [merchants, setMerchants] = useState<MerchantInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUserIdRef = useRef<string | undefined>(undefined);

  // Load merchants from API - called explicitly after login
  // userId is optional and will be used for user-specific storage
  const fetchMerchants = useCallback(async (userId?: string) => {
    setLoading(true);
    // Store userId for later use
    if (userId) {
      currentUserIdRef.current = userId;
    }
    const effectiveUserId = userId || currentUserIdRef.current;

    try {
      const response = await getPartners();
      // Handle Business API format: { success, message, data: Partner[] }
      const partners = response.data || [];

      if (partners.length > 0) {
        const merchantList: MerchantInfo[] = partners.map(
          partnerToMerchantInfo,
        );
        setMerchants(merchantList);

        // Save merchants list to localStorage for access.ts
        const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
        localStorage.setItem(
          merchantsListKey,
          JSON.stringify(
            merchantList.map((m) => ({ id: m.id, myRole: m.myRole })),
          ),
        );

        // Restore the entered/last-selected merchant, falling back to the first
        const merchantKey = getStorageKey(effectiveUserId, 'merchant');
        const targetId = resolveInitialMerchantId(
          merchantList,
          effectiveUserId,
        );
        const target = merchantList.find((m) => m.id === targetId);
        if (target) {
          setCurrentMerchant(target);
          localStorage.setItem(merchantKey, String(target.id));
        }

        return { hasMerchants: true, merchants: merchantList };
      }
      // No merchants available
      setMerchants([]);
      setCurrentMerchant(null);

      // Clear user-specific storage
      const merchantKey = getStorageKey(effectiveUserId, 'merchant');
      localStorage.removeItem(merchantKey);
      const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
      localStorage.removeItem(merchantsListKey);

      return { hasMerchants: false, merchants: [] };
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
      return { hasMerchants: false, merchants: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // Change current merchant
  const changeMerchant = useCallback(
    (merchantId: number) => {
      const merchant = merchants.find((m) => m.id === merchantId);
      if (merchant) {
        setCurrentMerchant(merchant);

        // Save to user-specific localStorage
        const merchantKey = getStorageKey(currentUserIdRef.current, 'merchant');
        localStorage.setItem(merchantKey, String(merchantId));

        // Handle detail page redirects - only redirect from actual detail pages
        const currentPath = window.location.pathname;

        // Check for balance accounts detail page (e.g., /balance-accounts/123)
        if (currentPath.match(/^\/balance-accounts\/\d+/)) {
          history.push('/balance-accounts');
          return;
        }

        // Check for partner detail page (e.g., /merchants/123) but NOT balance-accounts list
        const partnerDetailMatch = currentPath.match(/^\/merchants\/(\d+)$/);
        if (partnerDetailMatch) {
          const viewingPartnerId = Number.parseInt(partnerDetailMatch[1], 10);
          // If we're viewing the same partner we're switching to, stay on the page
          if (viewingPartnerId === merchantId) {
            return;
          }
          // Otherwise navigate to the new merchant's detail page
          history.push(`/merchants/${merchantId}`);
          return;
        }
      }
    },
    [merchants],
  );

  // Clear current merchant (for logout)
  const clearMerchant = useCallback(() => {
    // Clear user-specific storage
    const effectiveUserId = currentUserIdRef.current;
    if (effectiveUserId) {
      const merchantKey = getStorageKey(effectiveUserId, 'merchant');
      localStorage.removeItem(merchantKey);
      const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
      localStorage.removeItem(merchantsListKey);
    }

    // Reset state
    setCurrentMerchant(null);
    setMerchants([]);
    currentUserIdRef.current = undefined;
  }, []);

  // Add a new merchant and optionally switch to it.
  // userId is optional but should be passed by callers that may run before the
  // ref was seeded (SuperAdmin/Admin acting as an owner who had no merchants
  // yet: enterMerchantOwnerMode seeds the ref, but a full page reload drops it).
  const addMerchant = useCallback(
    (
      partner: BusinessAPI.Partner,
      switchTo: boolean = true,
      userId?: string,
    ) => {
      const newMerchant = partnerToMerchantInfo(partner);

      if (userId) {
        currentUserIdRef.current = userId;
      }
      const effectiveUserId = userId || currentUserIdRef.current;

      // Add to merchants list
      setMerchants((prev) => {
        // Check if already exists
        if (prev.some((m) => m.id === newMerchant.id)) {
          return prev;
        }
        return [...prev, newMerchant];
      });

      // Persist to the same localStorage list that access.ts (checkHasMerchants)
      // and app.tsx (checkUserHasMerchants) read. Without this the new merchant
      // lives only in React state, both guards still see "no merchants", and the
      // post-create redirect lands on a route the current mode forbids → 403.
      // Append (not overwrite) so an owner's existing merchants survive.
      if (effectiveUserId) {
        const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
        let stored: StoredMerchant[] = [];
        try {
          const raw = localStorage.getItem(merchantsListKey);
          const parsed = raw ? JSON.parse(raw) : [];
          if (Array.isArray(parsed)) {
            stored = parsed;
          }
        } catch {
          // Corrupt payload — rebuild from just the new merchant
        }
        if (!stored.some((m) => m?.id === newMerchant.id)) {
          stored = [
            ...stored,
            { id: newMerchant.id, myRole: newMerchant.myRole },
          ];
          localStorage.setItem(merchantsListKey, JSON.stringify(stored));
        }
      }

      // Switch to the new merchant if requested
      if (switchTo) {
        setCurrentMerchant(newMerchant);
        const merchantKey = getStorageKey(effectiveUserId, 'merchant');
        localStorage.setItem(merchantKey, String(newMerchant.id));
      }

      return newMerchant;
    },
    [],
  );

  // Load merchants directly (used by SuperAdmin merchant owner mode)
  const loadMerchants = useCallback(
    (merchantList: MerchantInfo[], userId?: string, selectId?: number) => {
      // Set userId ref so subsequent changeMerchant/clearMerchant use correct keys
      if (userId) {
        currentUserIdRef.current = userId;
      }
      setMerchants(merchantList);

      // Set current merchant directly to avoid stale closure in changeMerchant
      const target = selectId
        ? merchantList.find((m) => m.id === selectId)
        : merchantList[0];
      if (target) {
        setCurrentMerchant(target);
        const merchantKey = getStorageKey(
          userId || currentUserIdRef.current,
          'merchant',
        );
        localStorage.setItem(merchantKey, String(target.id));
      }

      const effectiveUserId = userId || currentUserIdRef.current;
      if (effectiveUserId) {
        const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
        localStorage.setItem(
          merchantsListKey,
          JSON.stringify(
            merchantList.map((m) => ({ id: m.id, myRole: m.myRole })),
          ),
        );
      }
    },
    [],
  );

  return {
    currentMerchant,
    merchants,
    loading,
    fetchMerchants,
    changeMerchant,
    clearMerchant,
    addMerchant,
    loadMerchants,
  };
}
